import { recordGateEntity } from './accessRules'
import { resolveAccess, resolveRecordGate } from './resolveAccess'
import { useAccessSources } from './useAccessSources'
import type { NonRecordAccessRule, RecordGate } from './accessRules'

const isRecordGate = (rule: NonRecordAccessRule | RecordGate): rule is RecordGate =>
  !('authorization' in rule) || rule.authorization.kind === 'record'

/**
 * Whether one rule admits the current caller, where withheld answers read as no. A surface that must
 * say something distinct while a check is in flight reads `useAccessSources` and calls `resolveAccess`
 * for the four-state answer instead.
 *
 * The overloads pair a record rule with the id it is asked about, so a rule naming a verb cannot reach
 * the evaluator without the record it names. The id may be absent — a page whose record is still
 * loading, or a tab whose request has no supplier — and answers no rather than throwing.
 */
export function useIsPermitted(rule: NonRecordAccessRule): boolean
export function useIsPermitted(rule: RecordGate, recordId: string | undefined): boolean
export function useIsPermitted(rule: NonRecordAccessRule | RecordGate, recordId?: string): boolean {
  const sources = useAccessSources(
    isRecordGate(rule) && recordId != null ? { entity: recordGateEntity(rule), recordId } : undefined
  )

  if (!isRecordGate(rule)) return resolveAccess(rule, sources) === 'allowed'
  if (recordId == null) return false

  return resolveRecordGate(rule, sources) === 'allowed'
}
